import { confirmable } from 'react-confirm';
import { useTranslation } from 'react-i18next';
import StackedModal from './stacked_modal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { TextField, Card } from '@shopify/polaris';
import i18n from 'src/locales/i18n';
import { ProductCard, ProductViewType } from 'src/components/cards/product_card';
import {
  fStringToStringDate,
  formatExpirationInput,
  isValidExpirationInput,
} from 'src/utils/format_time';
import { chooseLan } from 'src/utils/language_code';
import { requiredField } from 'src/utils/format_data';
import { fNumber } from 'src/utils/format_number';

i18n.loadNamespaces(['stock_management', 'common', 'validation']);

function StockDialog({ show, proceed, title, data }) {
  const { t } = useTranslation(['common', 'stock_management', 'validation']);

  const expirationDate = data?.expirationDt;

  const [inputValue, setInputValue] = useState({
    lotNumber: data?.lotNumber,
    expirationDt: data?.expirationDt,
    quantity: '',
    onHand: data?.onHandQuantity,
    available: data?.availableQuantity,
  });

  const defaultInputValue = useMemo(
    () => ({
      lotNumber: data?.lotNumber,
      expirationDt: fStringToStringDate(data?.expirationDt) || '',
      quantity: '',
      onHand: data?.onHandQuantity,
      available: data?.availableQuantity,
    }),
    [data],
  );

  const isDirty = useMemo(() => {
    return (
      inputValue.lotNumber !== defaultInputValue.lotNumber ||
      inputValue.expirationDt !== defaultInputValue.expirationDt ||
      inputValue.quantity !== defaultInputValue.quantity ||
      inputValue.onHand !== defaultInputValue.onHand ||
      inputValue.available !== defaultInputValue.available
    );
  }, [inputValue, defaultInputValue]);

  const maxAllowedQuantity = useMemo(() => {
    const available = parseInt(inputValue?.available) || 0;
    return available;
  }, [inputValue?.available]);

  const isQuantityInvalid = useMemo(() => {
    const isEmpty = inputValue?.quantity === '' || inputValue?.quantity == null;
    const isOverLimit = parseInt(inputValue?.quantity) > maxAllowedQuantity;
    return isEmpty || isOverLimit;
  }, [inputValue?.quantity, maxAllowedQuantity]);

  useEffect(() => {
    if (expirationDate) {
      setInputValue((oldValue) => ({
        ...oldValue,
        expirationDt: fStringToStringDate(expirationDate),
      }));
    } else {
      setInputValue((oldValue) => ({ ...oldValue, expirationDt: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const [error, setError] = useState('');

  const handleChange = useCallback((newValue, type) => {
    if (type === 'lot') setInputValue((oldValue) => ({ ...oldValue, lotNumber: newValue }));
    if (type === 'exp') setInputValue((oldValue) => ({ ...oldValue, expirationDt: newValue }));
    if (type === 'quant') setInputValue((oldValue) => ({ ...oldValue, quantity: newValue }));
    if (type === 'onHand') setInputValue((oldValue) => ({ ...oldValue, onHand: newValue }));
    if (type === 'available') setInputValue((oldValue) => ({ ...oldValue, available: newValue }));
  }, []);

  const handleChangeDate = (newValue) => {
    const formatted = formatExpirationInput(newValue);
    handleChange(formatted, 'exp');

    const digits = formatted.replace(/-/g, '');
    if (digits.length < 8) {
      setError('');
      return;
    }
    setError(isValidExpirationInput(formatted) ? '' : i18n.t('validation:exp_format_invalid'));
  };

  const handleBlur = () => {
    const value = inputValue?.expirationDt;
    if (value && !isValidExpirationInput(value)) {
      setError(i18n.t('validation:exp_format_invalid'));
    }
  };

  return (
    <StackedModal
      isOpen={show}
      title={title || t('common:button.confirm')}
      content={
        <Box
          display={'flex'}
          flexDirection={'column'}
          width={'100%'}
          rowGap={3}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
              width: '100%',
            }}
          >
            <Card style={{ display: 'flex', flexDirection: 'column' }}>
              <Box
                display={'flex'}
                flexDirection={'column'}
                rowGap={1}
                sx={{ flex: 1 }}
              >
                <Typography variant="body_md_semibold">{i18n.t('common:field.product')}</Typography>
                <ProductCard
                  product={data}
                  productViewType={ProductViewType.ROW_SMALL}
                  showBarcode
                  barcodeIcon
                  tooltip
                />
              </Box>
            </Card>

            <Card style={{ display: 'flex', flexDirection: 'column' }}>
              <Box
                display={'flex'}
                flexDirection={'column'}
                rowGap={2}
                sx={{ flex: 1 }}
              >
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                >
                  <Typography variant="body_md_semibold">
                    {i18n.t('stock_management:field.location')}
                  </Typography>
                  <Typography variant="body_md">
                    {chooseLan(data?.locationNameLocal, data?.locationNameEnglish)}
                  </Typography>
                </Box>
                <Box
                  display={'flex'}
                  flexDirection={'column'}
                >
                  <Typography variant="body_md_semibold">
                    {i18n.t('common:field.customer')}
                  </Typography>
                  <Typography variant="body_md">
                    {chooseLan(data?.userNameLocal, data?.userNameEnglish)}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Box>

          <Box
            className="readonly-field"
            display={'flex'}
            flexDirection={'row'}
            width={'100%'}
            columnGap={2}
          >
            <Box
              display={'flex'}
              width="50%"
              flexDirection={'column'}
            >
              <Typography
                variant="body_md_semibold"
                style={{ paddingBottom: '12px', textAlign: 'right' }}
              >
                {i18n.t('stock_management:field.on_hand')}
              </Typography>
              <TextField
                multiline={false}
                align="right"
                placeholder={i18n.t('stock_management:field.on_hand')}
                rows={1}
                value={inputValue?.onHand ? fNumber(inputValue.onHand) : '0'}
                disabled
              />
            </Box>
            <Box
              display={'flex'}
              flexDirection={'column'}
              width="50%"
            >
              <Typography
                variant="body_md_semibold"
                style={{ paddingBottom: '12px', textAlign: 'right' }}
              >
                {i18n.t('stock_management:field.available')}
              </Typography>
              <TextField
                multiline={false}
                align="right"
                placeholder={i18n.t('stock_management:field.available')}
                rows={1}
                value={inputValue?.available ? fNumber(inputValue.available) : '0'}
                disabled
              />
            </Box>
          </Box>

          <Box
            display={'flex'}
            flexDirection={'row'}
            width={'100%'}
            columnGap={2}
          >
            <Box
              display={'flex'}
              flexDirection={'column'}
              width="33.33%"
            >
              <Typography
                variant="body_md_semibold"
                style={{ paddingBottom: '12px', textAlign: 'right' }}
              >
                {requiredField(i18n.t('stock_management:field.change_quantity'))}
              </Typography>
              <TextField
                type="text"
                multiline={false}
                align="right"
                placeholder={`${i18n.t('stock_management:field.change_quantity')} (max: ${fNumber(maxAllowedQuantity)})`}
                rows={1}
                value={inputValue?.quantity ? fNumber(inputValue.quantity) : ''}
                onChange={(newValue) => {
                  const numbersOnly = newValue.replace(/\D/g, '');
                  if (numbersOnly === '') {
                    handleChange('', 'quant');
                    return;
                  }
                  const numericValue = parseInt(numbersOnly) || 0;
                  const limitedValue = Math.min(numericValue, maxAllowedQuantity);
                  handleChange(limitedValue, 'quant');
                }}
                onKeyDown={(e) => {
                  const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
                  const isNumber = /^[0-9]$/.test(e.key);

                  if (!isNumber && !allowedKeys.includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const paste = (e.clipboardData || window.clipboardData).getData('text');
                  const numbersOnly = paste.replace(/\D/g, '');
                  if (numbersOnly) {
                    const numericValue = parseInt(numbersOnly) || 0;
                    const limitedValue = Math.min(numericValue, maxAllowedQuantity);
                    handleChange(limitedValue, 'quant');
                  }
                }}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
              />
            </Box>
            <Box
              display={'flex'}
              flexDirection={'column'}
              width="33.33%"
            >
              <Typography
                variant="body_md_semibold"
                style={{ paddingBottom: '12px' }}
              >
                {i18n.t('stock_management:field.lot')}
              </Typography>
              <TextField
                multiline={false}
                rows={1}
                value={inputValue?.lotNumber}
                placeholder={i18n.t('stock_management:field.lot')}
                onChange={(newValue) => {
                  handleChange(newValue, 'lot');
                }}
              />
            </Box>
            <Box
              display={'flex'}
              flexDirection={'column'}
              width="33.33%"
            >
              <Typography
                variant="body_md_semibold"
                style={{ paddingBottom: '12px' }}
              >
                {i18n.t('stock_management:field.exp')}
              </Typography>
              <TextField
                multiline={false}
                placeholder={'YYYY-MM-DD'}
                rows={1}
                value={inputValue?.expirationDt}
                onBlur={handleBlur}
                error={error}
                onChange={handleChangeDate}
                maxLength={10}
                inputMode="numeric"
                onKeyDown={(e) => {
                  const allowedKeys = [
                    'Backspace',
                    'Delete',
                    'ArrowLeft',
                    'ArrowRight',
                    'Tab',
                    'Home',
                    'End',
                    'Enter',
                  ];
                  const isNumber = /^[0-9]$/.test(e.key);
                  if (!isNumber && !allowedKeys.includes(e.key) && !e.metaKey && !e.ctrlKey) {
                    e.preventDefault();
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      }
      primaryAction={{
        content: t('common:button.confirm'),
        disabled: !isDirty || error || isQuantityInvalid,
        onAction: () => {
          proceed({ confirmed: true, value: inputValue });
        },
      }}
      onClose={() => {
        proceed({ confirmed: false, value: null });
      }}
    />
  );
}

export default confirmable(StockDialog);
